/**
 * @file certificationSlice.js
 * @description
 * Slice para manejar el estado del flujo de certificación (exámenes), incluyendo pasos,
 * temporizador y su visibilidad, tiempo restante, y finalización automática.
 */

import { createSlice } from '@reduxjs/toolkit';

/**
 * Estado inicial del slice de certificación.
 */
const certificationInitialState = {
	step: 1, // Paso actual del examen
	timeOver: false, // Indica si el tiempo se ha agotado
	startTimer: false, // Controla si el temporizador debe iniciar
	showTimer: false, // Controla la visibilidad del temporizador
	remainingTime: 0 // Tiempo restante en segundos
};

const certificationSlice = createSlice({
	name: 'certificationInfo',
	initialState: certificationInitialState,
	reducers: {
		/**
		 * Incrementa el paso actual del examen.
		 * @param {Object} state - Estado actual del slice.
		 */
		incrementStep(state) {
			state.step += 1;
		},

		/**
		 * Controla el inicio o detención del temporizador.
		 * @param {Object} state - Estado actual.
		 * @param {Object} action - Acción con valor booleano.
		 */
		setStartTimer(state, action) {
			state.startTimer = action.payload;
		},

		/**
		 * Controla la visibilidad del temporizador en pantalla.
		 * @param {Object} state - Estado actual.
		 * @param {Object} action - Acción con valor booleano.
		 */
		setShowTimer(state, action) {
			state.showTimer = action.payload;
		},

		/**
		 * Establece el tiempo inicial del temporizador.
		 * @param {Object} state - Estado actual.
		 * @param {Object} action - Acción con valor numérico.
		 */
		setInitialTime(state, action) {
			state.remainingTime = action.payload;
		},

		/**
		 * Reinicia el temporizador y lo detiene.
		 * @param {Object} state - Estado actual.
		 * @param {Object} action - Acción con el nuevo tiempo.
		 */
		resetTimer(state, action) {
			state.startTimer = false;
			state.remainingTime = action.payload;
		},

		/**
		 * Marca que el tiempo se ha agotado y lo guarda en localStorage.
		 * @param {Object} state - Estado actual.
		 * @param {Object} action - Acción con valor booleano.
		 */
		setTimeOver(state, action) {
			state.timeOver = action.payload;
			localStorage.setItem('timeOver', action.payload.toString());
		},

		/**
		 * Resetea la sección actual (temporizador y visibilidad).
		 * @param {Object} state - Estado actual.
		 */
		resetSection(state) {
			state.startTimer = false;
			state.showTimer = false;
			state.remainingTime = 0;
		},

		/**
		 * Reinicia todo el flujo de certificación.
		 * @param {Object} state - Estado actual.
		 */
		resetCertification(state) {
			state.step = 1;
			state.timeOver = false;
			state.startTimer = false;
			state.showTimer = false;
			state.remainingTime = 0;
			localStorage.setItem('timeOver', 'false');
		}
	}
});

// Exporta las acciones y el reducer
export const certificationActions = certificationSlice.actions;
export default certificationSlice.reducer;